
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import displayINRCurrency from "../helpers/displayCurrency";
import OrderTimeline from "../components/OrderTimeline";
import { motion } from "framer-motion";
import {
  FaBoxOpen,
  FaRupeeSign,
  FaTruck,
  FaCheckCircle,
  FaDownload,
  FaTimes,
} from "react-icons/fa";

const OrderDetails = () => {

  const { id } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    fetchOrder();
  }, []);

  const fetchOrder = async () => {
    const res = await fetch(
      `https://lappyshop.onrender.com/api/order/${id}`,
      { credentials: "include" }
    );

    const data = await res.json();

    if (data.success) {
      setOrder(data.data);
    }
  };

  // 🔥 CANCEL ORDER
  const handleCancel = async () => {
    const res = await fetch(
      `https://lappyshop.onrender.com/api/cancel-order/${id}`,
      {
        method: "PUT",
        credentials: "include",
      }
    );

    const data = await res.json();

    if (data.success) {
      fetchOrder();
      alert("Order Cancelled ❌");
    }
  };

  // 🔥 INVOICE DOWNLOAD (basic)
  const handleInvoice = () => {
    window.print(); // simple invoice print
  };

  if (!order) return null;

  return (
    <div className="bg-[#f5f7fb] min-h-screen py-6 px-3">

      <div className="max-w-5xl mx-auto">

        {/* HEADER */}
        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-xl md:text-2xl font-bold mb-6"
        >
          Order Details
        </motion.h1>

        {/* MAIN CARD */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow p-5 md:p-6"
        >

          {/* 🔥 ACTION BUTTONS */}
          <div className="flex flex-wrap gap-3 mb-5">

            <button className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700">
              Track Order
            </button>

            <button
              onClick={handleInvoice}
              className="bg-gray-800 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2"
            >
              <FaDownload /> Invoice
            </button>

            {order.orderStatus !== "cancelled" && (
              <button
                onClick={handleCancel}
                className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2"
              >
                <FaTimes /> Cancel Order
              </button>
            )}

          </div>

          {/* TOP INFO */}
          <div className="grid md:grid-cols-2 gap-4 mb-5">

            <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-xl">
              <FaTruck className="text-blue-500" />
              <div>
                <p className="text-xs text-gray-500">Payment</p>
                <p className="font-medium text-sm">
                  {order.paymentMethod} - {order.paymentStatus}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-xl">
              <FaCheckCircle className="text-green-500" />
              <div>
                <p className="text-xs text-gray-500">Status</p>
                <p className="font-medium text-sm capitalize">
                  {order.orderStatus}
                </p>
              </div>
            </div>

          </div>

          {/* TIMELINE */}
          {order.orderStatus === "cancelled" ? (
            <div className="bg-red-100 text-red-600 p-4 rounded-xl mb-4 text-sm">
              ❌ This order has been cancelled
            </div>
          ) : (
            <OrderTimeline status={order.orderStatus} />
          )}

          {/* PRODUCTS */}
          <div className="mt-6">

            <h2 className="font-semibold mb-4 flex items-center gap-2">
              <FaBoxOpen /> Ordered Items
            </h2>

            <div className="space-y-3">

              {order.items.map((item) => (
                <motion.div
                  key={item._id}
                  whileHover={{ scale: 1.02 }}
                  className="flex gap-4 items-center bg-gray-50 p-4 rounded-xl"
                >

                  {/* IMAGE */}
                  <img
                    src={item.productId.productImage?.[0]}
                    className="w-16 h-16 object-contain rounded"
                  />

                  {/* DETAILS */}
                  <div className="flex-1">
                    <p className="font-medium text-sm md:text-base">
                      {item.productId.productName}
                    </p>

                    <p className="text-xs text-gray-500">
                      Qty: {item.quantity}
                    </p>
                  </div>

                  {/* PRICE */}
                  <p className="font-semibold text-red-600 text-sm md:text-base">
                    {displayINRCurrency(item.quantity * item.price)}
                  </p>

                </motion.div>
              ))}

            </div>

          </div>

          {/* TOTAL */}
          <div className="border-t mt-6 pt-4 flex justify-between items-center text-lg font-semibold">

            <div className="flex items-center gap-2">
              <FaRupeeSign />
              Total
            </div>

            <span className="text-green-600">
              {displayINRCurrency(order.totalAmount)}
            </span>

          </div>

        </motion.div>

      </div>

    </div>
  );
};

export default OrderDetails;